import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { ModuleId } from '@/lib/modules';

/**
 * Los módulos declarados en el registro y SIN CONSTRUIR (`unavailable: true` en
 * `MODULES`): no hay pantalla que cargar, así que tampoco hay página ni ruta.
 *
 * Se nombran a mano porque `MODULES` se declara con su tipo ancho —las pruebas
 * del servidor leen esa línea tal cual— y el compilador no puede ver ahí qué
 * entrada lleva el flag. Que esta lista y el registro digan lo mismo lo exige
 * `lib/niveles.test.ts`; y como `MODULE_PAGES` es un `Record`, el día que un
 * módulo se construya el compilador obliga a sacarlo de acá al escribir su
 * página.
 */
type ModuloSinConstruir = 'automations';

export type ModuloConPagina = Exclude<ModuleId, ModuloSinConstruir>;

/**
 * One chunk per module, written out by hand because a templated `import()` is
 * opaque to the bundler and would collapse the split. Adding a module to the
 * registry means adding its page here — and nowhere else. Un módulo sin
 * construir es la única excepción, y está dicha arriba en el tipo.
 */
export const MODULE_PAGES: Record<ModuloConPagina, LazyExoticComponent<ComponentType>> = {
  dashboard: lazy(() => import('@/features/extra/dashboard/page')),
  leads: lazy(() => import('@/features/extra/leads/page')),
  contacts: lazy(() => import('@/features/extra/contacts/page')),
  companies: lazy(() => import('@/features/extra/companies/page')),
  deals: lazy(() => import('@/features/extra/deals/page')),
  activities: lazy(() => import('@/features/extra/activities/page')),
  tasks: lazy(() => import('@/features/extra/tasks/page')),
  inbox: lazy(() => import('@/features/extra/inbox/page')),
  mail: lazy(() => import('@/features/extra/mail/page')),
  calls: lazy(() => import('@/features/extra/calls/page')),
  calendar: lazy(() => import('@/features/extra/calendar/page')),
  reports: lazy(() => import('@/features/extra/reports/page')),
  agents: lazy(() => import('@/features/base/agents/page')),
  escalation: lazy(() => import('@/features/base/escalation/page')),
  usage: lazy(() => import('@/features/base/usage/page')),
  traceability: lazy(() => import('@/features/base/traceability/page')),
  bases: lazy(() => import('@/features/base/bases/page')),
  tickets: lazy(() => import('@/features/extra/tickets/page')),
  settings: lazy(() => import('@/features/settings/page')),
};

/** Si el módulo tiene pantalla. Los que no, son exactamente los sin construir. */
export const tienePagina = (id: ModuleId): id is ModuloConPagina => Object.hasOwn(MODULE_PAGES, id);
